import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { MessageViewModel } from '@trovata/app/features/chat-assistant/models/chat-assistant-messages';
import {
	AddChatMessage,
	ClearChatConversation,
	ClearChatSessionsState,
	HydrateChatConversation,
	MigrateChatConversation,
	RemoveChatSession,
	SetChatSessionId,
	UpdateChatMessage,
} from '../actions/chat-sessions.actions';

export interface ChatConversation {
	messages: MessageViewModel[];
	sessionId: string | null;
}

export class ChatSessionsStateModel {
	conversations: { [contextKey: string]: ChatConversation };
}

const DEFAULT_CHAT_SESSIONS_STATE: ChatSessionsStateModel = {
	conversations: {},
};

@State<ChatSessionsStateModel>({
	name: 'chatSessions',
	defaults: DEFAULT_CHAT_SESSIONS_STATE,
})
@Injectable()
export class ChatSessionsState {
	@Selector()
	static conversations(state: ChatSessionsStateModel): { [contextKey: string]: ChatConversation } {
		return state.conversations;
	}

	@Action(AddChatMessage)
	addChatMessage(ctx: StateContext<ChatSessionsStateModel>, action: AddChatMessage): void {
		const state: ChatSessionsStateModel = ctx.getState();
		const existing: ChatConversation = state.conversations[action.contextKey] ?? { messages: [], sessionId: null };
		// The same response frame can be dispatched by more than one MessagingService instance; drop
		// the second copy. User messages carry no interactionId and may repeat.
		const isDuplicateResponse: boolean =
			!!action.message.interactionId && existing.messages.some((m: MessageViewModel) => m.interactionId === action.message.interactionId);
		if (isDuplicateResponse) {
			return;
		}
		ctx.patchState({
			conversations: {
				...state.conversations,
				[action.contextKey]: {
					...existing,
					messages: [...existing.messages, action.message],
				},
			},
		});
	}

	@Action(UpdateChatMessage)
	updateChatMessage(ctx: StateContext<ChatSessionsStateModel>, action: UpdateChatMessage): void {
		const state: ChatSessionsStateModel = ctx.getState();
		const existing: ChatConversation | undefined = state.conversations[action.contextKey];
		if (!existing) {
			return;
		}
		const updatedMessages: MessageViewModel[] = existing.messages.map((m: MessageViewModel) => (m.tempId === action.tempId ? { ...m, ...action.patch } : m));
		ctx.patchState({
			conversations: {
				...state.conversations,
				[action.contextKey]: {
					...existing,
					messages: updatedMessages,
				},
			},
		});
	}

	@Action(SetChatSessionId)
	setChatSessionId(ctx: StateContext<ChatSessionsStateModel>, action: SetChatSessionId): void {
		const state: ChatSessionsStateModel = ctx.getState();
		const existing: ChatConversation = state.conversations[action.contextKey] ?? { messages: [], sessionId: null };
		ctx.patchState({
			conversations: {
				...state.conversations,
				[action.contextKey]: {
					...existing,
					sessionId: action.sessionId,
				},
			},
		});
	}

	@Action(HydrateChatConversation)
	hydrateChatConversation(ctx: StateContext<ChatSessionsStateModel>, action: HydrateChatConversation): void {
		const state: ChatSessionsStateModel = ctx.getState();
		ctx.patchState({
			conversations: {
				...state.conversations,
				[action.contextKey]: {
					messages: [...action.messages],
					sessionId: action.sessionId,
				},
			},
		});
	}

	@Action(ClearChatConversation)
	clearChatConversation(ctx: StateContext<ChatSessionsStateModel>, action: ClearChatConversation): void {
		const state: ChatSessionsStateModel = ctx.getState();
		const { [action.contextKey]: _removed, ...remaining } = state.conversations;
		ctx.patchState({ conversations: remaining });
	}

	@Action(MigrateChatConversation)
	migrateChatConversation(ctx: StateContext<ChatSessionsStateModel>, action: MigrateChatConversation): void {
		const state: ChatSessionsStateModel = ctx.getState();
		const existing: ChatConversation | undefined = state.conversations[action.fromKey];
		if (!existing) {
			return;
		}
		const { [action.fromKey]: _removed, ...remaining } = state.conversations;
		ctx.patchState({
			conversations: {
				...remaining,
				[action.toKey]: { messages: existing.messages, sessionId: action.sessionId },
			},
		});
	}

	@Action(ClearChatSessionsState)
	clearChatSessionsState(ctx: StateContext<ChatSessionsStateModel>): void {
		ctx.setState(DEFAULT_CHAT_SESSIONS_STATE);
	}

	// Clears the stored conversation only if the deleted sessionId is the active one for that
	// context, so deleting a historical session doesn't wipe the chat the user is looking at.
	@Action(RemoveChatSession)
	removeChatSession(ctx: StateContext<ChatSessionsStateModel>, action: RemoveChatSession): void {
		const state: ChatSessionsStateModel = ctx.getState();
		const existing: ChatConversation | undefined = state.conversations[action.contextKey];
		if (!existing || existing.sessionId !== action.sessionId) {
			return;
		}
		const { [action.contextKey]: _removed, ...remaining } = state.conversations;
		ctx.patchState({ conversations: remaining });
	}
}
